import { create } from 'zustand'
import { getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'

export interface GPUStats {
  id: number
  name: string
  temperature: number
  utilization: number
  memoryUsed: number
  memoryTotal: number
  powerDraw: number
  powerLimit?: number
  fanSpeed?: number
}

interface LLMStats {
  model: string
  port: number
  status: 'online' | 'offline' | 'loading'
  requestsPerMinute: number
  averageLatency: number
  tokensGenerated: number
}

export interface SystemStats {
  gpus: GPUStats[]
  llms: LLMStats[]
  activeSessions: number
  totalMessages: number
  uptime: number
  cpuUsage: number
  memoryUsage: number
  memoryTotalMB?: number
  memoryUsedMB?: number
  timestamp?: string
}

export interface QueueStats {
  waiting: number
  active: number
  completed: number
  failed: number
  delayed: number
  paused: number
}

export interface RouterStats {
  messagesReceived: number
  messagesRouted: number
  messagesFailed: number
  messagesByType: Record<string, number>
  clients: number
  agents: number
  rules: number
  targets: number
  queues: number
}

export interface HealthInfo {
  status: 'healthy' | 'degraded'
  version: string
  uptime: number
  timestamp: string
  checks: {
    websocket: boolean
    redis: boolean
    queues: boolean
  }
  metrics: {
    connections: number
    sessions: number
    messagesReceived: number
    messagesSent: number
    errors: number
  }
}

interface SystemState {
  stats: SystemStats | null
  queueStats: QueueStats | null
  routerStats: RouterStats | null
  health: HealthInfo | null
  isLoading: boolean
  error: string | null
  lastUpdated: number | null
  fetchStats: () => Promise<void>
  fetchQueueStats: () => Promise<void>
  fetchRouterStats: () => Promise<void>
  fetchHealth: () => Promise<void>
}

function baseUrl(): string {
  const gw = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
  return gw || '/api'
}

export const useSystemStore = create<SystemState>((set) => ({
  stats: null,
  queueStats: null,
  routerStats: null,
  health: null,
  isLoading: false,
  error: null,
  lastUpdated: null,

  fetchStats: async () => {
    set({ isLoading: true })
    try {
      const response = await gatewayFetch(`${baseUrl()}/system/stats`)
      if (!response.ok) {
        throw new Error(`Gateway returned ${response.status}: ${response.statusText}`)
      }
      const data = await response.json()
      set({ stats: data, error: null, lastUpdated: Date.now() })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch stats'
      set((state) => ({ error: message, isLoading: false, stats: state.stats }))
    } finally {
      set({ isLoading: false })
    }
  },

  fetchQueueStats: async () => {
    try {
      const res = await gatewayFetch(`${baseUrl()}/queues/stats`)
      if (!res.ok) return
      set({ queueStats: await res.json() })
    } catch {
      // non-critical
    }
  },

  fetchRouterStats: async () => {
    try {
      const res = await gatewayFetch(`${baseUrl()}/router/stats`)
      if (!res.ok) return
      set({ routerStats: await res.json() })
    } catch {
      // non-critical
    }
  },

  fetchHealth: async () => {
    try {
      const res = await gatewayFetch(`${baseUrl()}/health`)
      if (!res.ok) return
      set({ health: await res.json() })
    } catch {
      // non-critical
    }
  },
}))
